// T4: Commercial-tenant employee workplace home.
//
// Covers Stitch v2 designs `employee_workplace_home_commercial_focus_1`
// and `employee_workplace_home_commercial_focus_2`. The two slugs differ
// only in tile density (small tenant vs. larger tenant); we render the
// same content responsively. Mode-gated to `commercial_multi_tenant`;
// reuses existing /me endpoints — no new backend.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { useBranding } from '../lib/branding.js';
import { humanizeAuditCode } from '../lib/auditLabels.js';
import {
  credentialStatusLabel,
  credentialTypeLabel,
  roleLabel,
} from '../lib/humanLabels.js';

interface MeProfile {
  user_id: string;
  name: string;
  email: string;
  role: string;
}

interface CredentialRow {
  id: string;
  type: string;
  status: string;
  label?: string | null;
}

interface EventRow {
  id: string;
  action: string;
  created_at: string;
  resource_type?: string;
}

interface ListResponse<T> {
  data: T[];
}

export function EmployeeWorkplaceHomePage() {
  const { state } = useAuth();
  const { term } = useBranding();
  const [credentials, setCredentials] = useState<CredentialRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const me: MeProfile | null =
    state.status === 'authed'
      ? {
          user_id: state.user.id,
          name: state.user.name,
          email: state.user.email,
          role: state.user.role,
        }
      : null;

  useEffect(() => {
    if (state.status !== 'authed') return;
    setLoading(true);
    setError(null);
    Promise.all([
      apiClient
        .get<ListResponse<CredentialRow>>('/api/v1/me/credentials')
        .catch(() => ({ data: [] as CredentialRow[] })),
      apiClient
        .get<ListResponse<EventRow>>('/api/v1/me/events?limit=10')
        .catch(() => ({ data: [] as EventRow[] })),
    ])
      .then(([c, e]) => {
        setCredentials(c.data);
        setEvents(e.data);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [state.status]);

  if (state.status !== 'authed') {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const activeCredCount = credentials.filter((c) => c.status === 'active').length;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Workplace</h1>
        <p className="text-on-surface-variant">
          Welcome, {me?.name ?? me?.email}. Your access, credentials, and recent activity.
        </p>
      </header>

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <Card>
          <CardBody>
            <p className="text-sm text-on-surface-variant">Active credentials</p>
            <p className="text-3xl font-semibold mt-1">{activeCredCount}</p>
            <Link to="/me/credentials" className="text-primary underline text-sm mt-2 inline-flex min-h-[1.5rem] items-center">
              View all
            </Link>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-on-surface-variant">Recent activity</p>
            <p className="text-3xl font-semibold mt-1">{events.length}</p>
            <Link to="/me/events" className="text-primary underline text-sm mt-2 inline-flex min-h-[1.5rem] items-center">
              View all
            </Link>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-on-surface-variant">Role</p>
            <p className="text-lg font-semibold mt-1">{roleLabel(me?.role)}</p>
            <p className="text-xs text-on-surface-variant mt-2">
              Operates as a {term('employee', 'Employee').toLowerCase()} in this tenant.
            </p>
          </CardBody>
        </Card>
      </div>

      <section>
        <h2 className="text-lg font-medium mb-2">Credentials</h2>
        <Card>
          <CardBody>
            {credentials.length === 0 ? (
              <p className="py-3 text-on-surface-variant">No credentials issued yet.</p>
            ) : (
              <ul className="divide-y divide-outline-variant">
                {credentials.map((c) => (
                  <li key={c.id} className="flex items-center justify-between py-2">
                    <span>
                      {c.label ?? credentialTypeLabel(c.type)}{' '}
                      <span className="text-on-surface-variant text-sm">
                        ({credentialTypeLabel(c.type)})
                      </span>
                    </span>
                    <Badge tone={c.status === 'active' ? 'success' : 'neutral'}>
                      {credentialStatusLabel(c.status)}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-2">Recent access events</h2>
        <Card>
          <CardBody>
            {events.length === 0 ? (
              <p className="py-3 text-on-surface-variant">No events yet.</p>
            ) : (
              <ul className="divide-y divide-outline-variant">
                {events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between py-2 text-sm">
                    <span>{humanizeAuditCode(e.action)}</span>
                    <span className="text-on-surface-variant">
                      {new Date(e.created_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}
